import {
  AnyAction,
  combineReducers,
  configureStore,
  Dispatch,
  ThunkDispatch
} from '@reduxjs/toolkit';
import thunk from 'redux-thunk';
import { persistStore, persistReducer, createTransform, PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import qs from 'qs';
import { initMessageListener } from 'redux-state-sync';
import _ from 'lodash';

import { BROWSER_REFRESH_TIMEOUT } from 'constants/constants';

import packageJson from '../../package.json';
import {
  oauthReducer,
  intlReducer,
  groupsReducer,
  clinicalLoggerReducer,
  patientReducer,
  settingsReducer,
  devicesReducer,
  notificationsReducer,
  tasksReducer,
  chatReducer,
  supportReducer,
  billingReducer
} from './';
import { initialState as settingsInitialState } from './settings/reducer';
import { initialState } from './groups/reducer';
import { initialState as clinicalLoggerInitialState } from './clinicalLogger/reducer';

const rootReducer = combineReducers({
  oauth: oauthReducer,
  intl: intlReducer,
  groups: groupsReducer,
  clinicalLogger: clinicalLoggerReducer,
  settings: settingsReducer,
  patient: patientReducer,
  devices: devicesReducer,
  notifications: notificationsReducer,
  tasks: tasksReducer,
  chat: chatReducer,
  support: supportReducer,
  billing: billingReducer
});

const shouldLogout = () => {
  if (!import.meta.env.VITE_BROWSER_CLOSE_TIMEOUT) {
    return false;
  }
  const tabsCount = parseInt(localStorage.getItem('tabsCount') || '0');
  const tabsClosedTimestamps: number[] = JSON.parse(
    localStorage.getItem('tabsClosedTimestamps') || '[]'
  );

  const refreshes = tabsClosedTimestamps.filter(
    (tabsClosedTimestamp: number) =>
      tabsClosedTimestamp + BROWSER_REFRESH_TIMEOUT > new Date().getTime()
  );
  localStorage.setItem('tabsClosedTimestamps', JSON.stringify(refreshes));

  return tabsCount <= 0 && refreshes.length === 0;
};

const handleWhiteList = () => {
  const whitelist = [
    'oauth',
    'intl',
    'settings',
    'groups',
    'tasks',
    'chat',
    'support',
    'clinicalLogger'
  ];
  const clientBuildVersion = localStorage.getItem('buildVersion');
  const buildVersion = `${packageJson.version}#${import.meta.env.VITE_GIT_VERSION}`;
  localStorage.setItem('buildVersion', buildVersion);
  if (clientBuildVersion && clientBuildVersion !== buildVersion) {
    /* eslint-disable no-console */
    console.log('New release detected, clearing localStorage');
    return [];
  }
  return whitelist;
};

const persistConfig: PersistConfig<any> = {
  key: 'root',
  storage,
  whitelist: handleWhiteList(),
  transforms: [
    createTransform(
      null,
      (state: IOAuthState) => {
        const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
        if (shouldLogout()) {
          return {
            data: null,
            loading: false,
            error: false,
            authType: state.authType,
            expiresAt: null,
            logoutRedirection: null
          };
        }
        return {
          data: params.authorizationCode ? params.authorizationCode : state.data,
          loading: false,
          error: false,
          authType: state.authType,
          expiresAt: state.expiresAt,
          state: state.logoutRedirection
        };
      },
      { whitelist: ['oauth'] }
    ),
    createTransform(
      null,
      (state: ISettingsState) => {
        const customViewsEditable = _.cloneDeep(state.customViews);
        customViewsEditable.patients.views = settingsInitialState.customViews.patients.views;
        customViewsEditable.patients.viewsOrder =
          settingsInitialState.customViews.patients.viewsOrder;

        return {
          ...state,
          hideNavigation: false,
          customViews: customViewsEditable,
          vitalsDailyOrderPreference: state.vitalsDailyOrderPreference || '1',
          loading: false,
          error: false,
          citc: null
        };
      },
      { whitelist: ['settings'] }
    ),
    createTransform(
      null,
      (state: ITranslationsState) => {
        return {
          ...state,
          loading: false,
          error: false,
          translations: null
        };
      },
      { whitelist: ['intl'] }
    ),
    createTransform(
      null,
      (state: IGroupsState) => {
        // this is not a good long-term solution. It should be integrated in getGroups instead
        return {
          ...initialState,
          selectedGid: state.selectedGid || 'all'
        };
      },
      { whitelist: ['groups'] }
    ),
    createTransform(
      null,
      (state: ITasksState) => {
        return {
          // only keep filters on refresh
          filters: state.filters
        };
      },
      { whitelist: ['tasks'] }
    ),
    createTransform(
      null,
      (state: IClinicalLoggerState) => {
        return {
          ...clinicalLoggerInitialState,
          notesOnly: state.notesOnly
        };
      },
      { whitelist: ['clinicalLogger'] }
    )
  ]
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const createReduxStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: [thunk],
    devTools: import.meta.env.DEV
  });
  const persistor = persistStore(store);

  initMessageListener(store);

  return { store, persistor };
};

export const { store, persistor } = createReduxStore();

/**
 * Dispatch for this store, thunks included. `typeof store.dispatch` is not enough: `middleware` is
 * given as a plain array, so RTK cannot infer thunk support from it and the inferred dispatch takes
 * `AnyAction` only. That inference gap is what `useDispatch<any>()` was standing in for.
 */
export type AppDispatch = ThunkDispatch<IAppState, unknown, AnyAction> & Dispatch<AnyAction>;

export default store;
